import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LayoutDashboard,
  Users,
  UserCheck,
  Package,
  CreditCard,
  GitFork,
  Archive,
  FlaskConical,
  Globe,
  FileText,
  ShieldAlert,
  Bell,
  LifeBuoy,
  Mail,
  ArrowRight,
  Command,
  X,
  UserCog,
  Layers,
  ShieldCheck,
  Server,
  Radio,
} from 'lucide-react';

interface CommandItem {
  id: string;
  title: string;
  category: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
}

const COMMANDS: CommandItem[] = [
  {
    id: 'dashboard',
    title: 'Painel de Controlo',
    category: 'Visão Geral',
    icon: LayoutDashboard,
    to: '/',
  },
  {
    id: 'registrations',
    title: 'Registos Pendentes',
    category: 'Clientes',
    icon: UserCheck,
    to: '/registrations',
  },
  { id: 'clients', title: 'Lista de Clientes', category: 'Clientes', icon: Users, to: '/clients' },
  {
    id: 'subscriptions',
    title: 'Subscrições & Quotas de Clientes',
    category: 'Clientes',
    icon: Layers,
    to: '/subscriptions',
  },
  {
    id: 'products',
    title: 'Catálogo de Produtos & APIs',
    category: 'Produtos',
    icon: Package,
    to: '/products',
  },
  {
    id: 'payment-methods',
    title: 'Métodos de Pagamento',
    category: 'Produtos',
    icon: CreditCard,
    to: '/payment-methods',
  },
  {
    id: 'kong-sync',
    title: 'Sincronização & Cluster Kong Gateway',
    category: 'Gateway',
    icon: Server,
    to: '/kong-sync',
  },
  {
    id: 'integration-routes',
    title: 'Rotas de Integração',
    category: 'Gateway',
    icon: GitFork,
    to: '/integration-routes',
  },
  {
    id: 'configuration',
    title: 'Backup & Configurações',
    category: 'Gateway',
    icon: Archive,
    to: '/configuration',
  },
  {
    id: 'sandbox-mocks',
    title: 'Sandbox Mocks',
    category: 'Gateway',
    icon: FlaskConical,
    to: '/sandbox-mocks',
  },
  {
    id: 'cors-approvals',
    title: 'Aprovações CORS',
    category: 'Gateway',
    icon: Globe,
    to: '/cors-approvals',
  },
  {
    id: 'api-logs',
    title: 'Logs de Requisições de API',
    category: 'Monitorização',
    icon: FileText,
    to: '/api-logs',
  },
  {
    id: 'webhooks-dlq',
    title: 'Webhooks & Dead Letter Queue (DLQ)',
    category: 'Monitorização',
    icon: Radio,
    to: '/webhooks-dlq',
  },
  {
    id: 'audit-logs',
    title: 'Logs de Auditoria e Segurança',
    category: 'Monitorização',
    icon: ShieldAlert,
    to: '/audit-logs',
  },
  {
    id: 'alerts',
    title: 'Alertas e Métricas do Sistema',
    category: 'Monitorização',
    icon: Bell,
    to: '/alerts',
  },
  {
    id: 'support',
    title: 'Tickets de Suporte',
    category: 'Suporte',
    icon: LifeBuoy,
    to: '/support',
  },
  {
    id: 'notifications',
    title: 'Templates de E-mail',
    category: 'Suporte',
    icon: Mail,
    to: '/notifications',
  },
  {
    id: 'admin-users',
    title: 'Equipa & Administradores (RBAC)',
    category: 'Equipa & Segurança',
    icon: UserCog,
    to: '/admin-users',
  },
  {
    id: 'mfa-setup',
    title: 'Configurar Segurança MFA (2FA)',
    category: 'Equipa & Segurança',
    icon: ShieldCheck,
    to: '/mfa-setup',
  },
];

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const filteredCommands = COMMANDS.filter((cmd) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    return cmd.title.toLowerCase().includes(q) || cmd.category.toLowerCase().includes(q);
  });

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev < filteredCommands.length - 1 ? prev + 1 : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : filteredCommands.length - 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selected = filteredCommands[selectedIndex];
        if (selected) {
          navigate(selected.to);
          onClose();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, navigate, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div
        className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden animate-scale-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input Bar */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <Search className="w-5 h-5 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pesquisar módulo, clientes, produtos ou logs..."
            className="flex-1 bg-transparent text-sm md:text-base text-slate-900 dark:text-white placeholder-slate-400 outline-none"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono text-[10px] text-slate-500 font-semibold">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div className="max-h-80 overflow-y-auto p-3 space-y-1 custom-scrollbar">
          {filteredCommands.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-500">
              Nenhum módulo ou resultado encontrado para &quot;{query}&quot;.
            </div>
          ) : (
            filteredCommands.map((item, idx) => {
              const isSelected = idx === selectedIndex;
              const Icon = item.icon;

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    navigate(item.to);
                    onClose();
                  }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl cursor-pointer text-sm transition-all ${
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-300 font-semibold'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'bg-primary-600 text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate">{item.title}</p>
                      <span className="text-[10px] text-slate-400 uppercase tracking-wider">
                        {item.category}
                      </span>
                    </div>
                  </div>
                  <ArrowRight
                    className={`w-4 h-4 shrink-0 transition-transform ${
                      isSelected
                        ? 'text-primary-600 dark:text-primary-300 translate-x-1'
                        : 'text-transparent'
                    }`}
                  />
                </div>
              );
            })
          )}
        </div>

        {/* Footer shortcuts helper */}
        <div className="px-5 py-2.5 bg-slate-50 dark:bg-slate-950/60 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-500">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono text-[9px] mr-1">
                ↑
              </kbd>
              <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono text-[9px] mr-1">
                ↓
              </kbd>
              Navegar
            </span>
            <span>
              <kbd className="px-1.5 py-0.5 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono text-[9px] mr-1">
                ↵
              </kbd>
              Selecionar
            </span>
          </div>
          <span className="flex items-center gap-1">
            <Command className="w-3 h-3" /> Atalho Global:{' '}
            <span className="font-semibold text-slate-700 dark:text-slate-300">Cmd + K</span>
          </span>
        </div>
      </div>
    </div>
  );
};
